import React, { useEffect } from 'react';
import { Button, Checkbox, DatePicker, Input, InputNumber, Radio } from 'antd';
import dayjs from 'dayjs';
import FileUpload from 'components/form/FileUploadPrivate';
import { fileNameFromKey } from 'utils/file';
import type { FileRef, MedicaidDelegated, MedicaidEnrollmentDto, MedicaidOwner } from '../types';
import { managerSsnValid } from '../validators';
import type { TMedicaidDerive, TMedicaidVariant } from './seed';
import { medicaidDerivedHint, seedMedicaidEnrollment } from './seed';
import s from './s.module.less';

interface Props {
    value?: MedicaidEnrollmentDto | null;
    onChange: (value: MedicaidEnrollmentDto) => void;
    variant: TMedicaidVariant;
    // California 专属题（Seller permit / Family PACT）
    isCA?: boolean;
    // 有法律实体时才问 operating agreement（entityHasAgreement）
    showOrgAgreement?: boolean;
    derive?: TMedicaidDerive;
}

type YNKey = {
    [K in keyof MedicaidEnrollmentDto]-?: MedicaidEnrollmentDto[K] extends boolean | null | undefined ? K : never;
}[keyof MedicaidEnrollmentDto];

const PLACE_OPTIONS = [
    { value: 'LEASED', label: 'I lease or rent it' },
    { value: 'OWNED', label: 'I own it' },
    { value: 'NEITHER', label: 'Neither, for example shared space or inside a hospital' },
];

const PAY_OPTIONS = [
    { value: 'DIRECT_DEPOSIT', label: 'Direct deposit to your payout account' },
    { value: 'PAPER_CHECK', label: 'Paper check' },
];

const Section = ({ title, children, desc }: { title: string; children: React.ReactNode; desc?: string }) => (
    <div className={s.section}>
        <div className={s.sectionTitle}>{title}</div>
        {desc && <p className={s.sectionDesc}>{desc}</p>}
        {children}
    </div>
);

// 原型 Medicaid enrollment（PAVE，09/21 起；10/15 加 4 项预填）：Medicaid 选 No 时打开的注册问卷。
// 题目全部不预选（原型：默认值等于替人答了），也都不拦 Continue——Klarity 代为注册，缺的由 ops 跟进。
// 三条路径的题目范围见 TMedicaidVariant；一份答案用于所有州
export default function MedicaidEnrollmentFields({
    value,
    onChange,
    variant,
    isCA,
    showOrgAgreement,
    derive = {},
}: Props) {
    const v = value || {};
    const set = (patch: Partial<MedicaidEnrollmentDto>) => onChange({ ...v, ...patch });

    // 预填只补还没答的字段；依据变化（如同一步里刚把某州改成已注册）时再补一次
    useEffect(() => {
        const seeded = seedMedicaidEnrollment(value, derive, variant);
        if (seeded) onChange(seeded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [derive.licenseActionNo, derive.everEnrolled, derive.payoutOnFile, variant]);

    const yn = (key: YNKey, question: string, hint?: string) => {
        const cur = v[key];
        let radio: 'Yes' | 'No' | undefined;
        if (cur === true) radio = 'Yes';
        else if (cur === false) radio = 'No';
        return (
            <div className={s.question}>
                <p className={hint ? s.questionTextWithHint : s.questionText}>{question}</p>
                {hint && <p className={s.hint}>{hint}</p>}
                <Radio.Group
                    value={radio}
                    onChange={(e) => set({ [key]: e.target.value === 'Yes' } as Partial<MedicaidEnrollmentDto>)}
                >
                    <Radio value="Yes">Yes</Radio>
                    <Radio value="No">No</Radio>
                </Radio.Group>
            </div>
        );
    };

    const choice = (
        key: 'placeOfBusiness' | 'payMethod',
        question: string,
        options: { value: string; label: string }[],
        hint?: string,
    ) => (
        <div className={s.question}>
            <p className={hint ? s.questionTextWithHint : s.questionText}>{question}</p>
            {hint && <p className={s.hint}>{hint}</p>}
            <Radio.Group
                className={s.choiceGroup}
                value={v[key] ?? undefined}
                onChange={(e) => set({ [key]: e.target.value } as Partial<MedicaidEnrollmentDto>)}
            >
                {options.map((o) => (
                    <Radio key={o.value} value={o.value}>
                        {o.label}
                    </Radio>
                ))}
            </Radio.Group>
        </div>
    );

    const text = (key: 'licenseNumber' | 'sellerPermitNumber' | 'glPolicyNumber', label: string, ph?: string) => (
        <div className={s.textField}>
            <span className={s.label}>{label}</span>
            <Input value={v[key] || ''} placeholder={ph} onChange={(e) => set({ [key]: e.target.value })} />
        </div>
    );

    const upload = (key: 'licenseFile' | 'workersCompFile' | 'glFile' | 'orgAgreementFile', label: string) => (
        <div className={s.upload}>
            <span className={s.label}>{label}</span>
            <FileUpload
                uploadType="LICENSE"
                accept=".pdf,.jpg,.jpeg,.png"
                tips="PNG, JPG, PDF up to 10MB"
                value={(v[key] as FileRef | null)?.url || ''}
                onChange={(url) => set({ [key]: url ? { url, name: fileNameFromKey(url) } : null })}
            />
        </div>
    );

    const business = (
        <>
            <Section title="Business">
                <Checkbox
                    className={s.check}
                    checked={!!v.licenseNotApplicable}
                    onChange={(e) => set({ licenseNotApplicable: e.target.checked })}
                >
                    My business does not have a business license
                </Checkbox>
                {!v.licenseNotApplicable && (
                    <>
                        {text('licenseNumber', 'Business license number', 'e.g. BL-123456')}
                        {upload('licenseFile', 'Business license')}
                    </>
                )}
                {yn('workersComp', 'Do you have workers compensation insurance?')}
                {v.workersComp === true && upload('workersCompFile', 'Workers compensation certificate')}
                {isCA && (
                    <>
                        {yn('sellerPermit', 'Do you have a seller permit?')}
                        {v.sellerPermit === true && text('sellerPermitNumber', 'Seller permit number')}
                    </>
                )}
                {choice('placeOfBusiness', 'Is your service location leased, owned, or neither?', PLACE_OPTIONS)}
                {text('glPolicyNumber', 'General liability insurance policy number')}
                {upload('glFile', 'General liability insurance policy')}
                {showOrgAgreement && (
                    <>
                        {upload('orgAgreementFile', 'Operating agreement')}
                        <div className={s.dateField}>
                            <span className={s.label}>Date it was signed</span>
                            <p className={s.hint}>
                                Some states only accept one signed in the last 12 months. If yours is older, re-sign and
                                date it before uploading.
                            </p>
                            <DatePicker
                                style={{ width: '100%' }}
                                format="MM/DD/YYYY"
                                value={v.orgAgreementSignedDate ? dayjs(v.orgAgreementSignedDate) : null}
                                disabledDate={(d) => d.isAfter(dayjs(), 'day')}
                                onChange={(d) => set({ orgAgreementSignedDate: d ? d.format('YYYY-MM-DD') : null })}
                            />
                        </div>
                    </>
                )}
            </Section>
            <Section title="Services">
                {yn('labServices', 'Will you bill Medicaid for laboratory services at this location?')}
                {isCA && yn('familyPact', 'Do you want to enroll in Family PACT?')}
            </Section>
            <Section title="Program participation">
                {yn('ownsOtherProviders', 'Does your business own or control any other health care provider?')}
                {v.ownsOtherProviders === true && (
                    <div className={s.textField}>
                        <span className={s.label}>List each one: legal name and street address</span>
                        <Input.TextArea
                            autoSize={{ minRows: 2 }}
                            value={v.otherProvidersList || ''}
                            onChange={(e) => set({ otherProvidersList: e.target.value })}
                        />
                    </div>
                )}
                {yn(
                    'everMedicaid',
                    'Have you ever participated in Medicaid in any state?',
                    medicaidDerivedHint(v, derive, 'everMedicaid'),
                )}
            </Section>
            <Section title="Business relationships">
                {yn('subcontractors', 'Do you have subcontractors that provide health care services or goods?')}
                {yn('txnLast5y', 'Any significant business transactions with a subcontractor in the last 5 years?')}
                {yn(
                    'txnOver25k',
                    'Any subcontractor transactions over $25,000 tied to Medicaid services in the last 12 months?',
                )}
                {yn('govDebt', 'Does your business owe any current state or federal government fines or debts?')}
            </Section>
            <Section title="Payment">
                {choice(
                    'payMethod',
                    'How should Medicaid pay you?',
                    PAY_OPTIONS,
                    medicaidDerivedHint(v, derive, 'payMethod'),
                )}
            </Section>
        </>
    );

    const disclosures = (
        <Section title="More disclosures">
            {yn(
                'civilFraud',
                'In the last 10 years, have you been found liable for fraud or abuse in a civil proceeding?',
            )}
            {yn('settlement', 'In the last 10 years, have you settled instead of being convicted of fraud or abuse?')}
            {yn(
                'licenseSurrendered',
                'Have you ever lost or surrendered a license while a disciplinary hearing was pending?',
                medicaidDerivedHint(v, derive, 'licenseSurrendered'),
            )}
            {yn(
                'licenseDisciplined',
                'Have you ever been disciplined by any licensing authority?',
                medicaidDerivedHint(v, derive, 'licenseDisciplined'),
            )}
        </Section>
    );

    if (variant === 'rendering') {
        return (
            <>
                <Section title="Program participation">
                    {yn(
                        'everMedicaid',
                        'Have you ever participated in Medicaid in any state?',
                        medicaidDerivedHint(v, derive, 'everMedicaid'),
                    )}
                </Section>
                {disclosures}
            </>
        );
    }

    if (variant === 'individual') {
        return (
            <>
                {business}
                {disclosures}
            </>
        );
    }

    // group：practice 级，多出 owners（21）/ delegated（9）/ signer（26），没有个人 disclosures
    const owners: MedicaidOwner[] = v.owners?.length ? v.owners : [{}];
    const setOwners = (next: MedicaidOwner[]) => set({ owners: next });
    const patchOwner = (i: number, patch: Partial<MedicaidOwner>) =>
        setOwners(owners.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
    const delegated: MedicaidDelegated[] = v.delegated?.length ? v.delegated : [{}];
    const setDelegated = (next: MedicaidDelegated[]) => set({ delegated: next });

    return (
        <>
            {business}
            <Section
                title="Owners and managing employees"
                desc="Everyone with 5% or more ownership or control, plus every officer, director and managing employee."
            >
                {owners.map((o, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className={s.listCard} key={o.id || `new-${i}`}>
                        <div className={s.listCardHeader}>
                            <span>Owner {i + 1}</span>
                            {owners.length > 1 && (
                                <Button
                                    type="link"
                                    size="small"
                                    className={s.linkBtn}
                                    onClick={() => setOwners(owners.filter((_, idx) => idx !== i))}
                                >
                                    Remove
                                </Button>
                            )}
                        </div>
                        <div className={s.ownerRow}>
                            <div>
                                <span className={s.label}>Full legal name</span>
                                <Input value={o.name || ''} onChange={(e) => patchOwner(i, { name: e.target.value })} />
                            </div>
                            <div>
                                <span className={s.label}>Title</span>
                                <Input
                                    value={o.title || ''}
                                    onChange={(e) => patchOwner(i, { title: e.target.value })}
                                />
                            </div>
                            <div>
                                <span className={s.label}>SSN or Tax ID</span>
                                <Input
                                    value={o.ssnOrTin || ''}
                                    placeholder="XXX-XX-XXXX"
                                    // 回填的是掩码：获得焦点清空重输，没输入就离开则还原（掩码 + id 原样带回 = 保留原值）
                                    onFocus={() => {
                                        if ((o.ssnOrTin || '').includes('•')) patchOwner(i, { ssnOrTin: '' });
                                    }}
                                    onBlur={() => {
                                        if (!(o.ssnOrTin || '').trim() && o.id && o.ssnOrTinLast4)
                                            patchOwner(i, { ssnOrTin: `•••-••-${o.ssnOrTinLast4}` });
                                    }}
                                    onChange={(e) =>
                                        patchOwner(i, { ssnOrTin: e.target.value.replace(/[^\d-]/g, '').slice(0, 11) })
                                    }
                                />
                                {!!(o.ssnOrTin || '').trim() && !managerSsnValid(o.ssnOrTin, o.id) && (
                                    <div className={s.fieldError}>Enter a 9-digit SSN or Tax ID.</div>
                                )}
                            </div>
                            <div>
                                <span className={s.label}>Ownership %</span>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    max={100}
                                    placeholder="e.g. 50"
                                    value={o.ownershipPct ?? null}
                                    onChange={(n) => patchOwner(i, { ownershipPct: n ?? null })}
                                />
                            </div>
                        </div>
                    </div>
                ))}
                <Button type="link" className={s.addLink} onClick={() => setOwners([...owners, {}])}>
                    + Add another person or entity
                </Button>
            </Section>
            <Section title="Delegated officials">
                {yn('hasDelegated', 'Can anyone besides the authorized official sign or update this enrollment?')}
                {v.hasDelegated === true && (
                    <>
                        {delegated.map((d, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div className={s.listCard} key={i}>
                                <div className={s.listCardHeader}>
                                    <span>Delegated official {i + 1}</span>
                                    {delegated.length > 1 && (
                                        <Button
                                            type="link"
                                            size="small"
                                            className={s.linkBtn}
                                            onClick={() => setDelegated(delegated.filter((_, idx) => idx !== i))}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </div>
                                <div className={s.delegatedRow}>
                                    <div>
                                        <span className={s.label}>Full legal name</span>
                                        <Input
                                            value={d.name || ''}
                                            onChange={(e) =>
                                                setDelegated(
                                                    delegated.map((x, idx) =>
                                                        idx === i ? { ...x, name: e.target.value } : x,
                                                    ),
                                                )
                                            }
                                        />
                                    </div>
                                    <div>
                                        <span className={s.label}>Title</span>
                                        <Input
                                            value={d.title || ''}
                                            onChange={(e) =>
                                                setDelegated(
                                                    delegated.map((x, idx) =>
                                                        idx === i ? { ...x, title: e.target.value } : x,
                                                    ),
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                        <Button type="link" className={s.addLink} onClick={() => setDelegated([...delegated, {}])}>
                            + Add another delegated official
                        </Button>
                    </>
                )}
            </Section>
            <Section
                title="Authorized signer"
                desc="The person legally allowed to sign for the practice. Their name must match their legal name exactly."
            >
                <div className={s.textField}>
                    <span className={s.label}>Full legal name</span>
                    <Input value={v.signerName || ''} onChange={(e) => set({ signerName: e.target.value })} />
                </div>
                <div className={s.textField}>
                    <span className={s.label}>Title</span>
                    <Input
                        value={v.signerTitle || ''}
                        placeholder="e.g. Owner, CEO"
                        onChange={(e) => set({ signerTitle: e.target.value })}
                    />
                </div>
            </Section>
        </>
    );
}
